import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { Carousel, Skeleton } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import Image, { type StaticImageData } from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo,useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import { countryAtom } from '../../../../atoms/countryState';
import { user } from '../../../../atoms/user';
// import { bookHomeHeroSectionCarouselData } from '../../../../utils/data';
import {
  type EventDetails,
  logEvent,
} from '../../../../hooks/useClickEventTracking';
import { getBannersByRoute } from '../../../../services/banner';
import type { User } from '../../../../utils/interfaces/User';
import ButtonDefault, { ButtonVariants } from '../../../shared/basic/button';
import { ShouldRender } from '../../../shared/basic/ShouldRender';

interface BookImageProps {
  book: string | StaticImageData;
  priority: boolean;
}

export interface BookProps {
  id: number;
  name: string;
  webFlowLink?: string;
  link?: string;
  desc: string;
  imageLG: StaticImageData;
  imageSM: StaticImageData;
  slug?: boolean;
}

type CustomCarouselRef = {
  next: () => void;
  prev: () => void;
  goTo: (slide: number) => void;
  autoPlay: (playType?: 'update' | 'leave' | 'blur' | undefined) => void;
  innerSlider: {
    goTo: (slide: number) => void;
  };
};

const generateUrlForImageBanner = (
  baseUrl: string,
  page: string,
  type: string,
): string => {
  return `${baseUrl}api/banner/images/${page}${type}`;
};

const BannerImage = React.memo(
  ({ book, priority }: BookImageProps) => {
    const screens = useBreakpoint();
    const imageWidth = 1340;
    const imageHeight = 440;
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
      if (priority && typeof book === 'string') {
        const preloadLink = document.createElement('link');
        preloadLink.rel = 'preload';
        preloadLink.as = 'image';
        preloadLink.href = book.toString();
        document.head.appendChild(preloadLink);

        return () => {
          document.head.removeChild(preloadLink);
        };
      }
      return undefined;
    }, [book, priority]);

    return (
      <>
        <div className='mx-3 lg:mx-0 rounded-xl'>
          <Image
            ref={imgRef}
            src={book || '/placeholder.svg'}
            alt={book as string || '/placeholder.svg'}
            width={imageWidth}
            height={imageHeight}
            loading={priority ? 'eager' : 'lazy'}
            sizes={
              screens.lg
                ? '100vw'
                : '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
            }
            priority={priority}
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              contentVisibility: priority ? 'auto' : 'auto',
            }}
            className='rounded-xl'
          />
        </div>
      </>
    );
  },
);

const HeroSection: React.FC<{
  books?: BookProps[];
  isSchool?: boolean;
  className?: string;
}> = ({ books: propsBooks, isSchool = false, className }) => {
  const isDefaultOrEmpty =
    !propsBooks ||
    propsBooks.length === 0;

  const [rawBanners, setRawBanners] = useState<any[]>([]);
  const countryState = useRecoilValue(countryAtom);
  const router = useRouter();

  const [loading, setLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const userData: User | undefined = useRecoilValue(user);
  const carouselRef = useRef<CustomCarouselRef | null>(null);
  const screens = useBreakpoint();

  useEffect(() => {
    if (!isDefaultOrEmpty) {
      setLoading(false);
      return;
    }

    const loadBanners = async () => {
      try {
        setLoading(true);
        const response = await getBannersByRoute(router.pathname);
        if (response && response.data) {
          setRawBanners(response.data);
        } else {
          setRawBanners([]);
        }
      } catch (error) {
        console.error('Error fetching route banners:', error);
        setRawBanners([]);
      } finally {
        setLoading(false);
      }
    };
    loadBanners();
  }, [router.pathname, isDefaultOrEmpty]);

  const normalizeCountry = (country: string): string => {
    const c = country.trim().toLowerCase();
    if (c === 'india') return 'india';
    if (c === 'canada') return 'canada';
    if (c === 'usa' || c === 'united states' || c === 'us') return 'usa';
    if (c === 'uk' || c === 'united kingdom' || c === 'gb') return 'uk';
    if (c === 'uae' || c === 'united arab emirates' || c === 'ae') return 'uae';
    if (c === 'other' || c === 'others') return 'other';
    return c;
  };

  const internalBanners = useMemo(() => {
    if (!isDefaultOrEmpty && propsBooks) return propsBooks;
    if (rawBanners.length === 0) return [];

    const userCountry = normalizeCountry(countryState.selectedCountry?.name || 'India');
    return rawBanners
      .filter((banner: any) => {
        const bannerCountry = banner.country ? normalizeCountry(banner.country) : 'other';
        if (bannerCountry === 'global' || bannerCountry === 'other') return true;
        return bannerCountry === userCountry;
      })
      .map((banner: any): BookProps => ({
        id: banner?._id,
        name: banner?.heading,
        webFlowLink: banner?.link,
        link: banner?.link,
        desc: banner?.desc || '',
        imageLG: generateUrlForImageBanner(
          `${process?.env.NEXT_PUBLIC_API_READER_URL}/`,
          `${banner?._id}/`,
          `image`,
        ) as any,
        imageSM: generateUrlForImageBanner(
          `${process?.env.NEXT_PUBLIC_API_READER_URL}/`,
          `${banner?._id}/`,
          `image`,
        ) as any,
        slug: banner?.slug === 'true',
      }));
  }, [rawBanners, countryState.selectedCountry?.name, propsBooks, isDefaultOrEmpty]);

  const books = isDefaultOrEmpty ? internalBanners : propsBooks!;

  useEffect(() => {
    carouselRef.current?.goTo(0);
  }, [books]);

  const getSourceValue = (path: string): string => {
    if (path === '/') {
      return 'home';
    } else if (path.includes('books')) {
      return 'books';
    } else if (path.includes('courses')) {
      return 'courses';
    } else if (path.includes('schools')) {
      return 'schools';
    } else if (path.includes('live')) {
      return 'live';
    } else {
      return 'home';
    }
  };

  if (loading) {
    return (
      <div
        className={twMerge(
          clsx(
            'carousel-container transition-all  duration-300 lg:px-6 xl:px-12',
            userData?.subscription
              ? 'mt-3 md:mt-8 lg:mt-2'
              : 'mt-3 md:mt-10 lg:mt-2',
          ),
          className,
        )}
      >
        <div
          style={{
            aspectRatio: '1344 / 440',
            height: 'auto',
          }}
          className='carousel-skeleton-container'
        >
          <div className='relative overflow-hidden'>
            <Skeleton.Button
              active={true}
              style={{
                aspectRatio: '1344 / 440',
                height: 'auto',
              }}
              className='carousel-skeleton-image rounded-xl lg:rounded-lg w-full px-3 lg:px-0'
            />
            <div className='absolute top-[25%] w-full left-[10%] md:left-[6%] lg:left-[3%]'>
              <Skeleton.Button
                style={{
                  width: screens.lg ? '15vw' : '45vw',
                  height: screens.lg ? '25px' : '15px',
                }}
                active
                size='large'
                className='rounded-md lg:rounded-lg'
              />
            </div>
            <ShouldRender check={screens.md}>
              <div className='absolute top-[40%] lg:top-[35%] w-full left-[10%] md:left-[6%] lg:left-[3%]'>
                <Skeleton.Button
                  style={{
                    width: screens.lg ? '25vw' : '55vw',
                    height: screens.lg ? '25px' : '15px',
                  }}
                  active
                  size='large'
                  className='rounded-lg'
                />
              </div>
            </ShouldRender>
            <div className='absolute bottom-[15%] left-[10%] md:left-[6%] lg:left-[3%]'>
              <Skeleton.Button
                style={{
                  width: screens.lg ? '150px' : '100px',
                  height: screens.lg ? '40px' : '25px',
                }}
                active
                size='large'
                className='rounded-md lg:rounded-lg'
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (books.length === 0) {
    return null;
  }

  return (
    <div
      className={twMerge(
        clsx(
          'carousel-container transition-all  duration-300 lg:px-6 xl:px-12',
          userData?.subscription
            ? 'mt-3 md:mt-8 lg:mt-2'
            : 'mt-3 md:mt-10 lg:mt-2',
        ),
        className,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='relative max-h-full'>
        <ShouldRender check={screens.lg && books?.length > 1}>
          <div className='flex absolute cursor-pointer z-30 bottom-[50%] -left-4 transform h-28 -translate-y-[-50%] lg:translate-y-[50%] items-center next-arrow'>
            <button
              name='back'
              aria-label='Go back'
              onClick={() => {
                carouselRef.current?.prev();
              }}
              className='z-10 w-8 h-8 xl:w-9 xl:h-9 rounded-full disabled:cursor-not-allowed flex cursor-pointer justify-center bg-gray-500 bg-opacity-40 items-center text-fadedWhite backdrop-filter backdrop-blur-md'
            >
              <ChevronLeftIcon className='w-4 h-4 xl:w-6 xl:h-6' />
            </button>
          </div>
        </ShouldRender>

        <div className='z-10'>
          <Carousel
            adaptiveHeight
            autoplay={!isHovered}
            dots={screens.lg ? false : true}
            arrows={false}
            lazyLoad='ondemand'
            style={{
              aspectRatio: '1344 / 440',
              height: 'auto',
              contain: 'layout',
            }}
            className='lg:rounded-2xl overflow-hidden'
            ref={carouselRef as React.RefObject<CustomCarouselRef>}
          >
            {books.map((book, index) => (
              <div
                key={`${book.id}-${index}`}
                className={clsx('flex justify-center w-full')}
              >
                <div
                  className='relative cursor-pointer w-full lg:w-full items-center flex-shrink-0'
                  style={{
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    aspectRatio: '1344 / 440',
                    height: 'auto',
                    paddingBottom: screens.md ? '26.87%' : '51.28%',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: 'fit',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                    }}
                    onClick={() => {
                      if (!isSchool) {
                        if (window !== undefined && book?.webFlowLink) {
                          window.open(book.webFlowLink, '_blank');
                        }
                        if (window !== undefined && book?.link && !isSchool) {
                          router.push(book.link);
                        }
                      }
                      const eventParams: EventDetails = {
                        event_name: 'banner_view',
                        source: getSourceValue(router.pathname),
                        source_type: getSourceValue(router.pathname),
                        source_id: null,
                        sub_source: null,
                        sub_source_id: null,
                        unit: 'boolean',
                        value: 1,
                      };
                      logEvent({
                        eventDetails: eventParams,
                        userData: userData,
                        pathname: router.pathname,
                      });
                    }}
                  >
                    <BannerImage
                      book={screens.sm ? book.imageSM : book.imageLG}
                      priority={index === 0}
                    />
                  </div>

                  <ShouldRender
                    check={screens.lg && !isSchool && book.slug === false}
                  >
                    <div className='absolute bottom-[15%] left-[3%]'>
                      <ButtonDefault
                        size={screens.lg ? 4 : 1}
                        rounded={true}
                        variant={ButtonVariants.WHITE}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (window !== undefined && book?.webFlowLink) {
                            window.open(book.webFlowLink, '_blank');
                          }
                          if (window !== undefined && book?.link) {
                            router.push(book.link);
                          }
                          const eventParams: EventDetails = {
                            event_name: 'banner_view',
                            source: getSourceValue(router.pathname),
                            source_type: getSourceValue(router.pathname),
                            source_id: null,
                            sub_source: null,
                            sub_source_id: null,
                            unit: 'boolean',
                            value: 1,
                          };
                          logEvent({
                            eventDetails: eventParams,
                            userData: userData,
                            pathname: router.pathname,
                          });
                        }}
                        className='w-min'
                      >
                        <span className='flex justify-center text-xs gap-x-2 xl:text-base px-2 lg:px-4 text-black whitespace-nowrap'>
                          Learn more
                        </span>
                      </ButtonDefault>
                    </div>
                  </ShouldRender>
                </div>
              </div>
            ))}
          </Carousel>
        </div>
        <ShouldRender check={screens.lg && books?.length > 1}>
          <div className='flex absolute cursor-pointer -right-4 bottom-[50%] transform h-28 -translate-y-[-50%] lg:translate-y-[50%] items-center next-arrow'>
            <button
              name='next'
              aria-label='Go next'
              onClick={() => {
                carouselRef.current?.next();
              }}
              className='z-10 w-8 h-8 xl:w-9 xl:h-9 rounded-full disabled:cursor-not-allowed flex cursor-pointer justify-center bg-gray-500 bg-opacity-40 items-center text-fadedWhite backdrop-filter backdrop-blur-md'
            >
              <ChevronRightIcon className='w-4 h-4 xl:w-6 xl:h-6' />
            </button>
          </div>
        </ShouldRender>
      </div>
    </div>
  );
};

export default HeroSection;
